import { type Dispatch, type SetStateAction, useMemo } from 'react';

import {
  Button,
  Divider,
  ExcelIcon,
  HelpCircleFillIcon,
  type RowSelectionState,
  Tooltip,
  type TypeSelectOption,
  type TypeTableProps,
} from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import {
  getReceiveReturnDisposalColumns,
  hasKurlyNowCenterRow,
  isClaimRequestBlocked,
  isRtsRequestBlocked,
  isScrapRequestBlocked,
} from '~/utils';

import {
  CLAIM_DEADLINE_DAYS,
  RECEIVE_PAGE_SIZE,
  RETURN_DISPOSAL_REQUEST_ACTIONS,
  RETURN_DISPOSAL_REQUEST_MESSAGE,
  type TypeReturnDisposalRequestAction,
} from '~/constants';

import type { TypePagination } from '~/types/common.types';
import type { TypeReturnDisposalInfo } from '~/types/receive.types';

import styles from './ReceiveReturnDisposalTable.module.scss';

type TypeReceiveReturnDisposalTableProps = {
  data: TypeReturnDisposalInfo[] | null;
  rowSelection: RowSelectionState;
  pagination: TypePagination;
  isLoading: boolean;
  isError: boolean;
  isDownloadReady: boolean;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onClickViewDetail: (data: TypeReturnDisposalInfo) => void;
  onClickExcelDownload: () => void;
  onChangeRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  onClickProcessRequest?: (action: TypeReturnDisposalRequestAction, selectedRows: TypeReturnDisposalInfo[]) => void;
};

/** 선택 불가 판정 2종 — 체크박스 비활성 조건과 마우스오버 안내 노출이 함께 움직여야 해서 공유한다. */
const isCompletedRow = (row: TypeReturnDisposalInfo): boolean => row.processStatus === 'COMPLETED';
const isScrapInProgressRow = (row: TypeReturnDisposalInfo): boolean =>
  row.processMethodCode === 'SCRAP' && row.processStatus === 'IN_PROGRESS';

/** 체크박스 선택 불가 조건 (처리완료 · 폐기 진행중) */
const isRowSelectable = (row: TypeReturnDisposalInfo): boolean => !isCompletedRow(row) && !isScrapInProgressRow(row);

/**
 * 선택 불가 행 체크박스 마우스오버 안내 — 처리완료와 폐기(진행중)은 사유가 달라 문구를 나눈다.
 * 참조가 바뀌면 kitchen이 선택 컬럼을 재계산하므로 모듈 상수로 둔다.
 */
const getSelectionTooltip: TypeTableProps<TypeReturnDisposalInfo>['selectionTooltip'] = (row) => {
  if (isCompletedRow(row.original)) {
    return { description: RETURN_DISPOSAL_REQUEST_MESSAGE.COMPLETED_ROW_TOOLTIP };
  }
  if (isScrapInProgressRow(row.original)) {
    return { description: RETURN_DISPOSAL_REQUEST_MESSAGE.SCRAP_IN_PROGRESS_ROW_TOOLTIP };
  }

  return undefined;
};

/**
 * [입고관리 > 회송/폐기 관리] 목록 테이블 (공통 Table)
 *
 * @note 빈 셀 '-' 표기·빈 목록 오버레이·로딩/에러는 공통 `Table`이 처리한다.
 *       검색 전(`data === null`, INIT)·결과 없음(빈 배열, EMPTY) 오버레이는 Table이 내부에서 구분한다.
 */
const ReceiveReturnDisposalTable = ({
  data,
  rowSelection,
  pagination,
  isLoading,
  isError,
  isDownloadReady,
  onChangePageLimit,
  onChangePage,
  onClickViewDetail,
  onClickExcelDownload,
  onChangeRowSelection,
  onClickProcessRequest,
}: TypeReceiveReturnDisposalTableProps) => {
  const columns = getReceiveReturnDisposalColumns({ onClickViewDetail });

  // Kitchen Table은 getRowId 미지원 → 현재 페이지 data와 인덱스로 매핑한다.
  const selectedRows = useMemo(
    () => (data ?? []).filter((_, index) => Boolean(rowSelection[String(index)])),
    [data, rowSelection]
  );
  const selectedCount = selectedRows.length;
  const isRequestDisabled = selectedCount === 0;

  // [클레임 신청] 전용: 선택 카드 중 1건이라도 이슈등록일 D+3(elapsedDays)을 초과하면 비활성.
  const isClaimDeadlineExceeded = selectedRows.some((row) => row.elapsedDays > CLAIM_DEADLINE_DAYS);

  // 액션별 전환 매트릭스 차단 판정 — 차단 조합은 returnDisposalActionGuards 정의부 참조.
  const isClaimBlocked = isClaimRequestBlocked(selectedRows);
  const isRtsBlocked = isRtsRequestBlocked(selectedRows);
  const isScrapBlocked = isScrapRequestBlocked(selectedRows) || hasKurlyNowCenterRow(selectedRows);

  const isActionDisabled = (action: TypeReturnDisposalRequestAction): boolean => {
    if (isRequestDisabled) return true;
    if (action === 'CLAIM') return isClaimDeadlineExceeded || isClaimBlocked;
    if (action === 'SCRAP') return isScrapBlocked;
    return isRtsBlocked;
  };

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        selectedCount={selectedCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            <Button
              variant="outlined"
              color="tertiary"
              size="tiny"
              disabled={!isDownloadReady}
              leftIcon={<ExcelIcon size={16} />}
              onClick={onClickExcelDownload}
            >
              엑셀 다운로드
            </Button>
            <Divider className={styles['divider']} orientation="vertical" color="gray100" />
            <Tooltip description={RETURN_DISPOSAL_REQUEST_MESSAGE.ACTION_TOOLTIP} maxWidth="360px">
              <HelpCircleFillIcon size={16} color="gray400" />
            </Tooltip>
            {RETURN_DISPOSAL_REQUEST_ACTIONS.map(({ key, label }) => (
              <Button
                key={key}
                variant="outlined"
                color="tertiary"
                size="tiny"
                disabled={isActionDisabled(key)}
                onClick={() => onClickProcessRequest?.(key, selectedRows)}
              >
                {label}
              </Button>
            ))}
          </div>
        }
      />
      <Table<TypeReturnDisposalInfo>
        className={styles['table']}
        data={data}
        columns={columns}
        rowHeight={40}
        height="100%"
        rowSelection={rowSelection}
        onRowSelectionChange={onChangeRowSelection}
        enableRowSelection={(row) => isRowSelectable(row.original)}
        // 목록을 훑다 셀을 눌렀을 뿐인데 처리신청 대상이 바뀌는 것을 막는다. 선택 수단은 체크박스 하나로 둔다.
        enableClickRowSelection={false}
        selectionTooltip={getSelectionTooltip}
        loading={isLoading}
        error={isError}
      />
      <TableBottomField
        pageSizeOptions={Object.values(RECEIVE_PAGE_SIZE)}
        currentPage={pagination.currentPage}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={onChangePage}
      />
    </div>
  );
};

export default ReceiveReturnDisposalTable;
